import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { cn } from '../lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline:
          'border bg-card text-foreground hover:bg-accent hover:text-accent-foreground shadow-sm',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        stamp: 'border-2 border-dashed border-primary/70 bg-transparent text-primary shadow-sm hover:bg-primary/5 rotate-[-1.5deg]',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export function Button({ className, variant, size, asChild = false, ...props }) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export function Card({ className, ...props }) {
  return (
    <div
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground flex flex-col gap-6 rounded-xl border shadow-paper',
        className
      )}
      {...props}
    />
  )
}

export const Input = forwardRef(function Input({ className, type, ...props }, ref) {
  return (
    <input
      ref={ref}
      type={type}
      data-slot="input"
      className={cn(
        'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-10 w-full min-w-0 px-2 py-1 text-base outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pencil-line',
        'focus-visible:ring-0 focus-visible:outline-none',
        className
      )}
      {...props}
    />
  )
})

export function WashiTape({ className, color = 'rgba(232, 220, 190, 0.6)', rotation = '-2deg' }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute h-6 w-20 opacity-80 shadow-sm',
        className
      )}
      style={{
        backgroundColor: color,
        transform: `rotate(${rotation})`,
        borderLeft: '1px solid rgba(255,255,255,0.4)',
        borderRight: '1px solid rgba(0,0,0,0.06)',
        clipPath:
          'polygon(2% 0, 98% 4%, 100% 100%, 0 96%)',
      }}
    />
  )
}

export function GolfBall({ color }) {
  const id = color.replace(/[^a-zA-Z0-9]/g, '')
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id={`sphere-${id}`} cx="38%" cy="32%" r="70%" fx="38%" fy="32%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="40%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor="black" stopOpacity="0.55" />
        </radialGradient>
        <clipPath id={`clip-${id}`}>
          <circle cx="14" cy="14" r="12" />
        </clipPath>
      </defs>
      <circle cx="14" cy="14" r="12" fill={`url(#sphere-${id})`} stroke="black" strokeWidth="1.2" />
      <g clipPath={`url(#clip-${id})`}>
        <circle cx="9" cy="9" r="1.5" fill="black" fillOpacity="0.22" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="14" cy="7.5" r="1.5" fill="black" fillOpacity="0.22" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="19" cy="9" r="1.5" fill="black" fillOpacity="0.22" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="6.5" cy="13.5" r="1.5" fill="black" fillOpacity="0.25" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="11" cy="12.5" r="1.5" fill="black" fillOpacity="0.25" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="16" cy="13" r="1.5" fill="black" fillOpacity="0.25" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="21" cy="13" r="1.5" fill="black" fillOpacity="0.25" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="8.5" cy="18" r="1.5" fill="black" fillOpacity="0.28" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="13.5" cy="17" r="1.5" fill="black" fillOpacity="0.28" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="18.5" cy="17.5" r="1.5" fill="black" fillOpacity="0.28" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="11" cy="21.5" r="1.5" fill="black" fillOpacity="0.3" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
        <circle cx="16" cy="21" r="1.5" fill="black" fillOpacity="0.3" stroke="black" strokeOpacity="0.18" strokeWidth="0.7" />
      </g>
      <circle cx="17.5" cy="7" r="1" fill="white" fillOpacity="0.55" clipPath={`url(#clip-${id})`} />
      <circle cx="14" cy="14" r="12" fill="none" stroke="black" strokeWidth="1.2" />
    </svg>
  )
}